import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { User } from '@shared/models/user/user';
import { MatDialog } from '@angular/material/dialog';
import { NewUserModalComponent } from '../new-user-modal/new-user-modal.component';
import { Region } from '@shared/models/region';
import { forkJoin, of, switchMap } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { SitesAreaService } from '@features/settings/sites-area/services/sites-area.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { UserService } from '../../services/user-service';

@Component({
    standalone: true,
    selector: 'app-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss'],
    imports: [CommonModule, FormsModule,MatIconModule, TranslateModule, AngularSvgIconModule],
})
export class UsersComponent implements OnInit {
    @Input() selectedUserId: string = null;
    @Input() selectedSiteId: number = null;
    @Input() selectedRegionId: number = null;
    @ViewChildren('rowElement') rowElements!: QueryList<ElementRef>;
    @ViewChild('userListContainer') userListContainer!: ElementRef;
    users: User[] = [];
    filteredUsers: User[] = [];
    areas: Region[] = [];
    expandedRowIndex: number | null = null;
    searchQuery = '';
    isEdit: boolean = false;

    constructor(private userService: UserService, private dialog: MatDialog, private cdr: ChangeDetectorRef, private location: Location,
        private sitesAreaService: SitesAreaService
     ) { }
     ngOnInit(): void {
       this.loadUsers();
      }
      loadUsers(): void {
        of(this.selectedSiteId).pipe(
            switchMap(siteId =>
              siteId
                ? this.sitesAreaService.getUsersBySiteId(siteId)
                : this.userService.getUsers()
            )
          ).subscribe({
            next: (users) => {
              this.users = users;
              this.filteredUsers = [...users];
              this.updateExpandedRow();
              this.cdr.detectChanges();
            },
            error: (error) => {
              this.users = [];
              this.filteredUsers = [];
              this.cdr.detectChanges();
            }
          });
      }

    onSearch(): void {
        const query = this.searchQuery.toLowerCase().trim();

        if (!query) {
            this.filteredUsers = [...this.users];
            return;
        }
        this.filteredUsers = this.users.filter(user => {
            const nameMatch = user.firstName && user.firstName.toLowerCase().includes(query);
            const emailMatch = user.email && user.email.toLowerCase().includes(query);
            const companyMatch = user.companyName && user.companyName.toLowerCase().includes(query);

            return nameMatch || emailMatch || companyMatch;
        });
    }

    clearSearch(): void {
        this.searchQuery = '';
        this.filteredUsers = [...this.users];
    }

    toggleRowDetails(index: number, userId: string): void {
        this.expandedRowIndex = this.expandedRowIndex === index ? null : index;

        if (this.expandedRowIndex === index) {
            forkJoin({
                sites: this.userService.getUserSites(userId),
                groups: this.userService.getUserGroups(userId)
            }).subscribe({
                next: ({ sites, groups }) => {
                    this.users[index].sites = sites;
                    const regionIds = sites.map(site => site.regionId);
                    this.users[index].areas = this.areas.filter(region => regionIds.includes(region.regionId));
                    this.users[index].groups = groups;

                    this.cdr.detectChanges();
                },
                error: (error) => {
                    console.error(`Error fetching details for user ${userId}:`, error);
                }
            });
        }
    }

    onNewUserClick(): void {
        const dialogRef = this.dialog.open(NewUserModalComponent, {
            width: '37.5rem !important',
            data: {selectedRegionId: this.selectedRegionId, selectedSiteId: this.selectedSiteId}
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.users.push(result);
                this.filteredUsers = [...this.users];
            }
        });
    }

    onModifyUser(user: User): void {
        this.userService.getRegionsByCurrentUser().subscribe((regions) => {
            this.areas = regions;
            this.userService.getUserSites(user.userId).subscribe((sites) => {
                user.sites = sites;
                const regionIds = sites.map(site => site.regionId);
                user.areas = this.areas.filter(region => regionIds.includes(region.regionId));
            });
        });
        const dialogRef = this.dialog.open(NewUserModalComponent, {
            width: '37.5rem !important',
            data: { user: user }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                const index = this.users.findIndex(u => u.userId === result.userId);
                if (index !== -1) {
                    this.users[index] = result;
                    this.filteredUsers = [...this.users];
                }
            }
        });
    }

    countVisibleItems(text: string, maxLength: number): number {
        if (text.length <= maxLength) return text.split(', ').length;

        let visibleText = text.slice(0, maxLength);
        let lastCommaIndex = visibleText.lastIndexOf(', ');
        if (lastCommaIndex !== -1) {
            visibleText = visibleText.slice(0, lastCommaIndex);
        }
        return visibleText.split(', ').length;
    }

    getSiteNames(user: User): string {
        var sites = user?.sites?.map(site => site.nom).join(', ') || '';
        return sites;
    }

    getRegionNames(user: User): string {
        var areas = user?.areas?.map(site => site.nom).join(', ') || '';
        return areas;
    }
    closeSettings() {
        this.location.back();
      }
      private updateExpandedRow(): void {
        if (this.users.length > 0 && this.selectedUserId) {
            const index = this.users.findIndex(user => user.userId === this.selectedUserId);
            if (index !== -1) {
                this.expandedRowIndex = index;
                this.cdr.detectChanges();
                this.scrollToExpandedRow();
            }
        }
    }
    private scrollToExpandedRow(): void {
        if (this.expandedRowIndex !== null && this.rowElements && this.userListContainer) {
            const row = this.rowElements.toArray()[this.expandedRowIndex];            
            if (row) {
                const rowElement = row.nativeElement;
                const container = this.userListContainer.nativeElement;
                const rowOffsetTop = rowElement.offsetTop;
                container.scrollTo({
                    top: rowOffsetTop - 200,
                    behavior: 'smooth'
                });
            }
        }
    }
}